import React, { useState, useEffect } from 'react';
import GSTR1Dashboard from '../GSTR1Dashboard';
import GSTPortalLogin from '../../components/gst/GSTPortalLogin';
import GSTPortalDashboard from '../../components/gst/GSTPortalDashboard';
import FileReturnsPage from '../../components/gst/FileReturnsPage';
import GSTR1AccessGate from '../../components/gst/GSTR1AccessGate';
import { trackActivity } from '../../lib/activity';

/**
 * GSTR-1 Simulator with Secure Access Gate
 * Flow: 
 * 1. Email Entry (Access Gate) -> sends credentials via email
 * 2. GST Portal Login -> user enters username/password from email
 * 3. OTP Modal -> user enters OTP from email
 * 4. Portal Dashboard -> File Returns -> GSTR-1 Dashboard
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default company data
const DEFAULT_COMPANY = {
  legalName: 'Demo Company Private Limited',
  tradeName: 'Demo Company',
  gstin: '07AAACG1234A1ZM',
  state: 'Delhi',
  filingPreference: 'Monthly'
};

const Gstr1Simulator = () => {
  const [accessStep, setAccessStep] = useState('email'); // email -> login -> portal flow
  
  // Track simulator start (logged-in only, fire-and-forget)
  useEffect(() => {
    trackActivity({
      simulator_id: 'gstr1',
      simulator_name: 'GSTR-1 Filing Simulator',
      action: 'started',
    });
  }, []);
  
  const [accessEmail, setAccessEmail] = useState('');
  const [step, setStep] = useState('portal'); // portal -> fileReturns -> dashboard
  const [companyData, setCompanyData] = useState(DEFAULT_COMPANY);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  // Check for existing valid access on mount
  useEffect(() => {
    const savedExpiry = localStorage.getItem('gstr1_access_expires');
    const savedEmail = localStorage.getItem('gstr1_access_email');
    
    if (savedExpiry && savedEmail) {
      const expiry = new Date(savedExpiry);
      if (expiry > new Date()) {
        // Already has valid access - skip to portal
        setAccessEmail(savedEmail);
        setAccessStep('portal');
      } else {
        // Clear expired access
        localStorage.removeItem('gstr1_access_email');
        localStorage.removeItem('gstr1_access_password');
        localStorage.removeItem('gstr1_access_expires');
        localStorage.removeItem('gstr1_pending_email');
      }
    }
  }, []);

  // Load company data from localStorage (from GST Registration)
  useEffect(() => {
    const savedData = localStorage.getItem('practiq_gst_company');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setCompanyData({ ...DEFAULT_COMPANY, ...parsed });
      } catch (e) {
        console.error('Error loading company data:', e);
      }
    }
  }, []);

  // Handle email entry completion - proceed to GST Portal Login
  const handleEmailSubmitted = (info) => {
    setAccessEmail(info.email);
    setAccessStep('login');
  };

  // Handle GST Portal Login with credential verification + OTP
  const handleLogin = async (credentials) => {
    // credentials = { username, password }
    // This is where we verify against our backend
    // The OTP modal is shown inside GSTPortalLogin component
    
    // Save access info
    localStorage.setItem('gstr1_access_email', credentials.username);
    localStorage.setItem('gstr1_access_password', credentials.password);
    
    // Move to portal
    setAccessStep('portal');
    setStep('portal');
  };

  // Handle logout - reset to email entry step
  const handleLogout = () => {
    // Clear all access-related localStorage
    localStorage.removeItem('gstr1_access_email');
    localStorage.removeItem('gstr1_access_password');
    localStorage.removeItem('gstr1_access_expires');
    localStorage.removeItem('gstr1_pending_email');
    localStorage.removeItem('gstr1_last_request_time');
    localStorage.removeItem('gstr1_last_request_email');
    localStorage.removeItem('pw_batch_email');
    localStorage.removeItem('practiq_lead_email');
    localStorage.removeItem('gstr1_access_granted');
    
    // Reset to email entry step
    setAccessEmail('');
    setAccessStep('email');
    setStep('portal');
    setSelectedPeriod(null);
  };

  const handleNavigateToFileReturns = () => {
    setStep('fileReturns');
  };

  const handleSelectReturn = (returnType, periodData) => {
    setSelectedPeriod(periodData);
    if (returnType === 'GSTR1') {
      setStep('dashboard');
    }
  };

  // Step 1: Email Entry (Access Gate)
  if (accessStep === 'email') {
    return <GSTR1AccessGate onAccessGranted={handleEmailSubmitted} />;
  }

  // Step 2: GST Portal Login (with credential verification + OTP)
  if (accessStep === 'login') {
    return (
      <GSTPortalLogin 
        onLogin={handleLogin} 
        formType="GSTR-1" 
        accessEmail={accessEmail}
        useSecureAccess={true}
      />
    );
  }

  // Step 3: GST Portal Dashboard
  if (step === 'portal') {
    return (
      <GSTPortalDashboard 
        companyData={companyData} 
        onNavigate={handleNavigateToFileReturns}
        onLogout={handleLogout}
        formType="GSTR-1"
      />
    );
  }

  // Step 4: File Returns Page
  if (step === 'fileReturns') {
    return (
      <FileReturnsPage
        companyData={companyData}
        onSelectReturn={handleSelectReturn}
        formType="GSTR-1"
      />
    );
  }

  // Step 5: GSTR-1 Dashboard
  return (
    <GSTR1Dashboard 
      skipAuth={true} 
      companyData={companyData} 
      selectedPeriod={selectedPeriod}
      onNavigateToPortal={() => setStep('portal')}
      onNavigateToFileReturns={() => setStep('fileReturns')}
      onLogout={handleLogout}
    />
  );
};

export default Gstr1Simulator;